import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { emailConnectionApi, YahooSMTPRequest } from '../api/emailApi';

interface YahooSMTPModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const YahooSMTPModal: React.FC<YahooSMTPModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<YahooSMTPRequest>({
    email: '',
    password: '',
    smtpHost: 'smtp.mail.yahoo.com',
    smtpPort: 587,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.email || !formData.password) {
      toast.error('Email and App Password are required');
      return;
    }

    if (!formData.smtpHost) {
      toast.error('SMTP Host is required');
      return;
    }

    if (!formData.smtpPort || formData.smtpPort <= 0) {
      toast.error('Valid port number is required');
      return;
    }

    setLoading(true);

    try {
      await emailConnectionApi.connectYahooSMTP(formData);
      toast.success('Yahoo account connected successfully!');
      setFormData({
        email: '',
        password: '',
        smtpHost: 'smtp.mail.yahoo.com',
        smtpPort: 587,
      });
      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to connect Yahoo email';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        email: '',
        password: '',
        smtpHost: 'smtp.mail.yahoo.com',
        smtpPort: 587,
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">Connect Yahoo Email</h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Email */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Email Address *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your-email@yahoo.com"
              disabled={loading}
              className="w-full bg-white border border-strivre-lightnavy rounded-xl px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all disabled:bg-slate-50 disabled:text-slate-500"
            />
            <p className="text-xs text-slate-500">Your Yahoo email address</p>
          </div>

          {/* App Password */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">App Password *</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="••••••••"
              disabled={loading}
              className="w-full bg-white border border-strivre-lightnavy rounded-xl px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all disabled:bg-slate-50 disabled:text-slate-500"
            />
            <p className="text-xs text-slate-500">
              Generate an app password in your{' '}
              <a
                href="https://login.yahoo.com/account/security"
                target="_blank"
                rel="noopener noreferrer"
                className="text-strivre-burgundy hover:text-blue-700 font-semibold"
              >
                Yahoo Account Security
              </a>
            </p>
          </div>

          {/* SMTP Host */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">SMTP Host</label>
            <input
              type="text"
              value={formData.smtpHost}
              onChange={(e) => setFormData({ ...formData, smtpHost: e.target.value })}
              placeholder="smtp.mail.yahoo.com"
              disabled={loading}
              className="w-full bg-white border border-strivre-lightnavy rounded-xl px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all disabled:bg-slate-50 disabled:text-slate-500"
            />
            <p className="text-xs text-slate-500">Default: smtp.mail.yahoo.com</p>
          </div>

          {/* Port */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Port</label>
            <input
              type="number"
              value={formData.smtpPort}
              onChange={(e) => setFormData({ ...formData, smtpPort: parseInt(e.target.value) || 587 })}
              placeholder="587"
              disabled={loading}
              className="w-full bg-white border border-strivre-lightnavy rounded-xl px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all disabled:bg-slate-50 disabled:text-slate-500"
            />
            <p className="text-xs text-slate-500">Default: 587 (TLS)</p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2.5 text-slate-700 bg-strivre-softgrey hover:bg-slate-200 rounded-xl font-semibold transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-strivre-burgundy hover:bg-red-900 disabled:bg-red-800 text-white rounded-xl font-semibold transition-all"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
